import { EditorContextService } from './EditorContextService';

const SELECTED_COMPONENT_VARIANTS = 'selectedComponentVariants';

export const onMessage = (
  listeners: { [key: string]: (() => void)[] },
  action: string,
  callback: (...args: unknown[]) => void | unknown
) => {
  if (!action || typeof callback !== 'function') {
    return;
  }
  const actionLowerCase = action.toLowerCase();
  listeners[actionLowerCase] = listeners[actionLowerCase] || [];
  listeners[actionLowerCase].push(callback);
};

export function getBaseServerUrl(serverUrl: string | URL): string {
  const url = new URL(serverUrl);
  return `${url.protocol}//${url.hostname}${url.port ? `:${url.port}` : ''}${url.pathname.substring(0, url.pathname.indexOf('.magnolia'))}`;
}

function getActionListeners(
  listeners: { [x: string]: (() => void)[] },
  action: string
): (() => void)[] {
  if (!action) {
    return [];
  }
  return listeners[action.toLowerCase()] || [];
}

function registerPageEditor(
  callback?: (...args: unknown[]) => void | unknown,
  serverUrl?: string
) {
  const id = 'mgnlPageEditorBridge';
  let bridgeScript: HTMLScriptElement = document.getElementById(
    id
  ) as HTMLScriptElement;
  if (bridgeScript || !serverUrl) {
    if (typeof callback === 'function') {
      callback();
    }
    return;
  }
  const hostUrl = getBaseServerUrl(serverUrl);
  const firstScript: HTMLScriptElement =
    document.getElementsByTagName('script')[0];
  bridgeScript = document.createElement('script');
  bridgeScript.id = id;
  bridgeScript.src = `${hostUrl}VAADIN/widgetsets/info.magnolia.PageEditor/info.magnolia.PageEditor.nocache.js`;
  firstScript.parentNode?.insertBefore(bridgeScript, firstScript);
  bridgeScript.onload = () => {
    // Wait for the page editor is ready and call necessary functions
    setTimeout(function waitAndCall() {
      if (EditorContextService.isPageEditorReady()) {
        EditorContextService.onFrameReady();
        EditorContextService.refresh();
        if (typeof callback === 'function') {
          callback();
        }
      } else {
        setTimeout(waitAndCall, 100);
      }
    }, 50);
  };

  const style = document.createElement('link');
  style.href = `${hostUrl}VAADIN/themes/pages-app/page-editor.css`;
  style.type = 'text/css';
  style.rel = 'stylesheet';
  document.head.insertBefore(style, document.head.firstChild);
}

export const requestServerUrl = () => {
  const notifyMessage = {
    element: {
      dialog: '',
      path: '/',
      workspace: 'website',
    },
    action: 'initialize',
    elementType: 'page',
  };
  window.parent.postMessage(JSON.stringify(notifyMessage), '*');
};

export const init = (
  listeners: { [key: string]: (() => void)[] },
  callback?: (...args: unknown[]) => void | unknown
) => {
  if (typeof window === 'undefined' || EditorContextService.isSameOrigin()) {
    return;
  }
  window.addEventListener('message', event => {
    if (
      !event.data ||
      !(typeof event.data === 'string' && event.data.startsWith('{'))
    )
      return;
    const message = JSON.parse(event.data);
    const actionListeners = getActionListeners(listeners, message.action);
    actionListeners.forEach((listener: (arg0: unknown) => void) => {
      try {
        listener(message);
      } catch (error) {
        console.error(error);
      }
    });
  });
  onMessage(listeners, 'updateState', (message: unknown) => {
    const serverUrl = (message as { serverUrl: string }).serverUrl;
    EditorContextService.setServerUrl(serverUrl);
    registerPageEditor(callback, serverUrl);
  });
  requestServerUrl();
};

const isSelectedComponentVariantsValid = (
  selectedComponentVariants: { [key: string]: string } | null,
  currentSearchParams: URLSearchParams
) => {
  const isValid =
    typeof selectedComponentVariants === 'object' &&
    Object.keys(selectedComponentVariants as { [key: string]: string }).length >
      0;
  if (!isValid) {
    return false;
  }
  const urlSelectedVariantsStr = JSON.stringify(selectedComponentVariants);
  return (
    urlSelectedVariantsStr !==
    currentSearchParams.get(SELECTED_COMPONENT_VARIANTS)
  );
};

export const initialVariantsHandler = (
  listeners: { [key: string]: (() => void)[] },
  useHistoryPushState?: boolean
) => {
  onMessage(listeners, 'updateState', msg => {
    const message = msg as {
      selectedComponentVariants: { [key: string]: string } | null;
    };
    const url = new URL(window.location.href);
    if (
      !isSelectedComponentVariantsValid(
        message.selectedComponentVariants,
        url.searchParams
      )
    ) {
      return;
    }
    url.searchParams.set(
      SELECTED_COMPONENT_VARIANTS,
      JSON.stringify(message.selectedComponentVariants)
    );
    if (useHistoryPushState) {
      window.history.pushState({}, '', url.toString());
      window.dispatchEvent(new Event('popstate'));
    } else {
      window.location.href = url.toString();
    }
  });
};
